import React from 'react';
import styled, { css } from 'styled-components';
import { InputGroup } from 'react-bootstrap';

import type { IFormSelectGroup } from './types';

import { ArrowLeft, ArrowRight } from '@/assets/icons';
import { flex, media } from '@/styles/theme';
import { SIDEBAR_DELAY } from '@/config/variables';

const SelectGroup = ({
  options,
  defaultValue,
  onLeftClick,
  onRightClick,
  blockOnLeftClick = false,
  blockOnRightClick = false,
  className = '',
}: IFormSelectGroup): React.ReactElement => {
  return (
    <StyledContainer className={className}>
      <InputGroup.Text className="arrow-button">
        <NavButton
          className="button-prev"
          onClick={() => !blockOnLeftClick && onLeftClick()}
          blockOnLeftClick={blockOnLeftClick}
        />
      </InputGroup.Text>
      <RangeLabel>{options.find(item => item.value === defaultValue)?.label}</RangeLabel>
      <InputGroup.Text className="arrow-button">
        <NavButton
          className="button-next"
          onClick={() => !blockOnRightClick && onRightClick()}
          blockOnRightClick={blockOnRightClick}
        />
      </InputGroup.Text>
    </StyledContainer>
  );
};

export { SelectGroup };

const navButton = (isBlocked: boolean, image: string) => {
  return css`
    cursor: pointer;
    position: relative;

    &:after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate3d(-50%, -50%, 0);
      width: 36px;
      height: 34px;
      background-image: url(${image});
      background-position: center center;
      background-repeat: no-repeat;
    }

    ${isBlocked &&
    css`
      pointer-events: none;
      cursor: default !important;

      &:after {
        filter: grayscale(100%) opacity(0.3);
      }
    `}
  `;
};

const StyledContainer = styled(InputGroup)`
  display: flex;
  flex-direction: row;
  margin-bottom: 6px;

  &.scheduling {
    width: 68%;
  }

  .arrow-button {
    padding: 0;
    border: none;
    background: none;
  }

  .input-group-prepend {
    margin-right: 0;
  }
`;

const NavButton = styled.button<{ blockOnLeftClick?: boolean; blockOnRightClick?: boolean }>`
  width: 36px;
  height: 34px;
  padding: 0;
  border: 1px solid #ced4da;
  background-color: #fff;
  background-position: center center;
  background-repeat: no-repeat;
  transition: width ${SIDEBAR_DELAY}ms ease-in-out, border-radius ${SIDEBAR_DELAY}ms ease-in-out;
  cursor: pointer;

  &:active {
    background-position: 52% 54%;
  }

  ${media.phone} {
    width: 52px;
    height: 46px;
  }

  &.button-prev {
    border-radius: 10px 0 0 10px;

    & .collapsed {
      width: 38px;
      border-radius: 5px 5px 0 0;
    }

    ${({ blockOnLeftClick }) => navButton(!!blockOnLeftClick, ArrowLeft)}
  }

  &.button-next {
    border-radius: 0 10px 10px 0;

    & .collapsed {
      width: 38px;
      border-radius: 0 5px 5px 0;
    }

    ${({ blockOnRightClick }) => navButton(!!blockOnRightClick, ArrowRight)}
  }
`;

const RangeLabel = styled.div.attrs({
  className: `${flex.centered} flex-grow-1`,
})`
  height: 34px;
  font-size: 14px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  padding: 0 4px;

  border: 1px solid #ced4da;
  border-left: none;
  border-right: none;
  transition: width ${SIDEBAR_DELAY}ms ease-in ${SIDEBAR_DELAY / 2}ms, opacity ${SIDEBAR_DELAY}ms ease;

  .collapsed & {
    width: 0;
    opacity: 0;
  }

  ${media.phone} {
    height: 46px;
  }
`;
